import React, { useEffect, useState } from 'react';
import { ScrollView, View, ActivityIndicator, Image } from 'react-native';
import { translate } from '../../services/translate';
import MyAppText from '../../components/MyAppText';
import MyRadio from '../../components/MyRadio';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import Tooltip from '../../components/Tooltip';
import Toast from 'react-native-toast-message';
import { useNavigationState } from '@react-navigation/native';
import { crmService } from '../../services/central-api/crm';
import DeleteModal from '../../components/DeleteModal';
import FormInput from '../../components/FormInput';
import { ClientError } from '../../services/central-api/base-service';
import { useAuth } from '../../context/Auth';
import Container from '../../components/Container';
import Header from '../../components/Header';
import CustomButton from '../../components/CustomButton';
import Card from '../../components/Card';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import InternetCompanyDropdown from './ManagementDetails/InternetCompanyDropdown';
import InstallationCompanyDropdown from './ManagementDetails/installationCompanyDropdown';

interface AlarmCenterDetailsParameters {
    navigation: Navigation;
    route: {
        params?: {
            id: string;
        };
    };
}

export default function AlarmCenterDetails({ navigation, route }: AlarmCenterDetailsParameters) {
    const { styles, theme } = useStyles(styleSheet);

    const { user: authenticatedUser } = useAuth();

    const index = useNavigationState(state => state);
    const [isLoading, setIsLoading] = useState(false);
    const [alreadyLoaded, setAlreadyLoaded] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);
    const [isDeleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);

    // fields
    const [title, setTitle] = useState('');
    const [address, setAddress] = useState('');
    const [latitude, setLatitude] = useState('');
    const [longitude, setLongitude] = useState('');
    const [serialNo, setSerialNo] = useState('');
    const [internetType, setInternetType] = useState<InternetType>('4g');
    const [installationCompanyId, setInstallationCompanyId] = useState(0);
    const [internetCompanyId, setInternetCompanyId] = useState(0);
    const [installer, setInstaller] = useState('');
    const [networkType, setNetworkType] = useState<NetworkType>('ipv6');

    async function getAlarmCenter(id: string) {
        try {
            setIsLoading(true);
            const alarmCenter = await crmService.getAlarmCenter(id);
            setTitle(alarmCenter.title);
            setAddress(alarmCenter.address);
            setLatitude(String(alarmCenter.latitude));
            setLongitude(String(alarmCenter.longitude));
            setSerialNo(alarmCenter.serialNo);
            setInternetType(alarmCenter.internetType);
            setInstaller(alarmCenter.installer);
            setNetworkType(alarmCenter.networkType);
            setInstallationCompanyId(alarmCenter.installationCompanyId);
            setInternetCompanyId(alarmCenter.internetCompanyId);
        } catch (err) {
            console.error(err);
        } finally {
            setAlreadyLoaded(true);
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (route.params?.id && !alreadyLoaded) {
            getAlarmCenter(route.params.id);
        }
    }, [route.params]);

    useEffect(() => {
        if (title === '') {
            return setIsFormValid(false);
        }
        if (address === '') {
            return setIsFormValid(false);
        }
        if (installer === '' && !route.params?.id) {
            return setIsFormValid(false);
        }
        if (serialNo === '' && !route.params?.id) {
            return setIsFormValid(false);
        }
        if (latitude.trim() === '' || Number.isNaN(Number(latitude))) {
            return setIsFormValid(false);
        }
        if (longitude.trim() === '' || Number.isNaN(Number(longitude))) {
            return setIsFormValid(false);
        }
        if (Number.isNaN(Number(installationCompanyId)) || installationCompanyId == 0) {
            return setIsFormValid(false);
        }
        if (Number.isNaN(Number(internetCompanyId)) || internetCompanyId == 0) {
            return setIsFormValid(false);
        }

        return setIsFormValid(true);
    }, [title, address, latitude, longitude, installer, networkType, serialNo, installationCompanyId, internetCompanyId]);

    async function save() {
        try {
            setIsLoading(true);
            if (route.params?.id) {
                await crmService.updateAlarmCenter(route.params.id, {
                    title,
                    address,
                    latitude: Number(latitude),
                    longitude: Number(longitude),
                    serialNo,
                    internetType,
                    installer,
                    installationCompanyId: installationCompanyId,
                    internetCompanyId: internetCompanyId,
                    networkType,
                });
            } else {
                await crmService.createAlarmCenter({
                    title,
                    address,
                    latitude: Number(latitude),
                    longitude: Number(longitude),
                    serialNo,
                    internetType,
                    installationCompanyId: installationCompanyId,
                    internetCompanyId: internetCompanyId,
                    installer,
                    networkType,
                });
            }
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            if (index.routes.length > 1) {
                return navigation.goBack();
            } else {
                window.location.href = '/CRM/alarm-centers';
            }
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <Container>
            <Header
                title={translate('alarmCenter')}
                goBackAction={() => {
                    if (index.routes.length > 1) {
                        return navigation.goBack();
                    } else {
                        window.location.href = '/CRM/alarm-centers';
                    }
                }}
                actions={<>
                    {route.params?.id &&
                        <CustomButton
                            onPress={() => setDeleteModalVisible(true)}
                            text={translate('delete')}
                            type='destructive'
                        />
                    }
                    <CustomButton
                        onPress={save}
                        text={translate('save')}
                        disabled={!isFormValid}
                    />
                </>}
            />

            <Card>
                <ScrollView style={{ padding: 24 }} contentContainerStyle={{ rowGap: 15 }}>
                    {isLoading &&
                        <ActivityIndicator size='small' color={theme.colors.loadingColor} />
                    }
                    <View style={{ flex: 1, flexWrap: 'wrap', flexDirection: 'row', columnGap: 40, rowGap: 10 }}>
                        <View style={{ rowGap: 20, maxWidth: 400, width: '100%' }}>
                            <View style={styles.generalInformationContainer}>
                                <FormInput
                                    label={translate('title')}
                                    placeholder=''
                                    value={title}
                                    onChangeText={setTitle}
                                    invalid={() => title === ''}
                                    required={true}
                                />
                            </View>
                            <View style={styles.generalInformationContainer}>
                                <FormInput
                                    label={translate('address')}
                                    placeholder=''
                                    value={address}
                                    onChangeText={setAddress}
                                    invalid={() => address === ''}
                                    required={true}
                                />
                            </View>
                            <View style={styles.generalInformationContainer}>
                                <FormInput
                                    label={translate('latitude')}
                                    placeholder=''
                                    value={latitude}
                                    onChangeText={setLatitude}
                                    invalid={() => latitude === ''}
                                    required={true}
                                />
                            </View>
                            <View style={styles.generalInformationContainer}>
                                <FormInput
                                    label={translate('longitude')}
                                    placeholder=''
                                    value={longitude}
                                    onChangeText={setLongitude}
                                    invalid={() => longitude === ''}
                                    required={true}
                                />
                            </View>
                            <View style={[styles.generalInformationContainer, { flexDirection: 'row', alignItems: 'center', columnGap: 5 }]}>
                                <View style={{ width: '95%' }}>
                                    <FormInput
                                        label={translate('serialNo')}
                                        placeholder=''
                                        value={serialNo}
                                        onChangeText={setSerialNo}
                                        invalid={() => serialNo === ''}
                                        maxLength={9}
                                        required={true}
                                    />
                                </View>
                                <View style={{ paddingTop: 24 }}>
                                    <Tooltip tooltipDirection='right' style={{ width: 600 }}
                                        tooltipContent={
                                            <View style={{ flexDirection: 'column', rowGap: 10, alignItems: 'center', paddingVertical: 5 }}>
                                                <MyAppText>{translate('hikSerialNoExplanation')}</MyAppText>
                                                <Image source={require('../../../assets/hikvision_serial_no.png')} style={{ height: 300, width: 580, objectFit: 'contain' }} />
                                            </View>
                                        }>
                                        <FontAwesomeIcon icon={faInfoCircle} color={theme.colors.textColor} />
                                    </Tooltip>
                                </View>
                            </View>
                            <View style={[styles.generalInformationContainer, { rowGap: 5 }]}>
                                <MyAppText style={styles.generalInformationTitleText}>
                                    {translate('networkType')}
                                </MyAppText>
                                <MyRadio
                                    selected={networkType}
                                    setSelected={setNetworkType}
                                    options={[{ value: 'ipv6', label: 'IPv6' }, { value: 'ipv4', label: 'IPv4' }]}
                                />
                            </View>
                            <View style={[styles.generalInformationContainer, { zIndex: 5, rowGap: 5 }]}>
                                <InstallationCompanyDropdown
                                    required={true}
                                    setValue={setInstallationCompanyId}
                                    value={installationCompanyId}
                                    zIndex={5}
                                    disabled={false}
                                />
                            </View>
                            <View style={styles.generalInformationContainer}>
                                <FormInput
                                    label={translate('installer')}
                                    placeholder=''
                                    value={installer}
                                    disabled={!(authenticatedUser?.isAdmin) && !!(route.params?.id)}
                                    onChangeText={setInstaller}
                                    invalid={() => installer === ''}
                                    required={true}
                                />
                            </View>
                        </View>
                        <View style={{ rowGap: 20, maxWidth: 400, width: '100%' }}>
                            <View style={[styles.generalInformationContainer, { rowGap: 5 }]}>
                                <MyAppText style={styles.generalInformationTitleText}>
                                    {translate('internetType')}
                                </MyAppText>
                                <MyRadio
                                    selected={internetType}
                                    setSelected={setInternetType}
                                    options={[
                                        { label: translate('fiber'), value: 'fiber' },
                                        { label: '5G', value: '5g' },
                                        { label: '4G', value: '4g' },
                                        { label: translate('radio'), value: 'radio' }
                                    ]}
                                />
                            </View>
                            <View style={[styles.generalInformationContainer, { zIndex: 4, rowGap: 5 }]}>
                                <InternetCompanyDropdown
                                    required={true}
                                    setValue={setInternetCompanyId}
                                    value={internetCompanyId}
                                    zIndex={4}
                                    disabled={false}
                                />
                            </View>
                        </View>
                    </View>
                </ScrollView>
            </Card>

            <DeleteModal
                setModalVisible={setDeleteModalVisible}
                isModalVisible={isDeleteModalVisible}
                itemName={serialNo ?? title}
                onSubmit={async () => {
                    try {
                        if (route.params?.id) {
                            await crmService.deleteAlarmCenter(route.params?.id);
                            Toast.show({
                                type: 'sentinelxSuccess',
                                text1: translate('ActionSuccessfully'),
                            });
                            if (index.routes.length > 1) {
                                return navigation.goBack();
                            } else {
                                window.location.href = '/CRM/alarm-centers';
                            }
                        }
                    } catch (err) {
                        if (err instanceof ClientError) {
                            return Toast.show({
                                type: 'sentinelxError',
                                text1: translate(err.message),
                            });
                        }

                        console.error(err);
                        Toast.show({
                            type: 'sentinelxError',
                            text1: translate('unexpectedError'),
                        });
                    }
                }}
            />
        </Container>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    generalInformationContainer: {
        minHeight: 40,
    },
    generalInformationTitleText: {
        color: theme.colors.labelColor,
    },
    modalContainer: {
        flex: 1,
        backgroundColor: '#31313199'
    },
}));
